/**
 * `browser_scan` — a tiny, query-driven perception primitive for FAST-DRIVE mode.
 *
 * Fast-drive agents act through raw Playwright (`browser_run_code`) over a
 * ref-less, un-mutated DOM. On hard widgets (ambiguous/duplicated labels, zero-
 * width-space text, controls hidden inside horizontal scroll strips, action
 * targets that are DESCENDANTS of the matched text) they burn dozens of
 * screenshot/`elementFromPoint` probes. `browser_scan` replaces that grind: given
 * a text `query` (+ optional `targetRole`/`relation`), it does ONE bounded DOM
 * traversal, gates to real control boundaries, ranks, and returns a single
 * verified, durable, ready-to-act locator — or, when ambiguous, the ranked
 * candidates plus guidance on how to narrow.
 *
 * This is NOT the aria-ref substrate. It is a fast `find` command. It never
 * dead-ends the model: every result carries a `status` + `guidance`.
 *
 * Design contract (see plan `ok-let-s-plan-all-mighty-octopus.md`):
 * - Anchor ≠ target: `query` locates the anchor; the action target may be a
 *   descendant (`relation:"descendant"`, e.g. a per-row "select only" button).
 * - Rank before reveal; reveal + trial-confirm the WINNER only.
 * - Read-only except a rare, gated, short-lived `data-fdref` stamp when no
 *   durable locator exists.
 * - Hard budgets: one `page.evaluate`, ≤ MAX_CANDIDATES, driver probes on the
 *   top few, trial-click only the winner. Find+verify is capped at
 *   `SCAN_DEADLINE_MS` (1500 ms) and degrades to `partial`; the winner reveal then
 *   gets its OWN cap (`2 × REVEAL_CAP_MS`), so a `ready` result's true worst-case
 *   wall-clock is ~2.5 s (find/verify + reveal). It degrades, never hangs.
 *
 * @module playwright-client/fast-drive-scan
 */

import type { Locator, Page } from 'playwright';
import type { RaceAgainstSignal } from './fast-drive-signals.js';
import { guidanceFor } from './fast-drive-scan-format.js';

export { formatScanResultText } from './fast-drive-scan-format.js';
export { shouldRenderScanVisual, scanHighlightBoxes } from './fast-drive-scan-visual.js';

/** How the action target relates to the text anchor named by `query`. */
export type ScanRelation = 'self' | 'descendant' | 'row';

export interface FastDriveScanInput {
  /**
   * Text anchor to locate (resolved value; zero-width/whitespace tolerant).
   * Provide EITHER `query` OR `at` (coordinate-probe), not both.
   */
  query?: string;
  /**
   * Coordinate-probe: a viewport pixel the agent is pointing at (from a
   * screenshot it can see). Scan hit-tests the point, walks up to the actionable
   * control boundary, and returns a durable locator for it — NOT a coordinate
   * click. Use for icons/controls the agent can SEE but cannot name.
   */
  at?: { x: number; y: number };
  /** ARIA role of the action target when it differs from the anchor. */
  targetRole?: string;
  /** Text/accessible-name of the action target (for `descendant`/`row`). */
  targetText?: string;
  /** Relation of the action target to the anchor. Default `self`. */
  relation?: ScanRelation;
  /**
   * The variable/binding expression the agent used for `query` (e.g.
   * `"vars.option_three"`). When present, the returned locator string
   * substitutes the binding for the resolved literal so `cacheCode` stays
   * dynamic. Read-only hint; does not affect matching.
   */
  queryBinding?: string;
  /**
   * Force the annotated context crop in the result (see the executor's visual
   * gate). Independent of the auto-triggers (`ambiguous` / coordinate-probe).
   * Read by the tool layer only; the core traversal ignores it.
   */
  visualize?: boolean;
  /** Allow the rare ephemeral `data-fdref` stamp fallback. Off for the scout. */
  allowStamp?: boolean;
  /** Allow revealing (scrolling) the winner into view. Off for the scout. */
  allowReveal?: boolean;
  /**
   * Reveal-only: scroll the winner into view but SKIP the trial-click
   * actionability confirm. Used by the run_code failure-repair path (which runs
   * on every bad click) — the agent's own retry confirms actionability, so we
   * don't pay a speculative actionability wait on the hot path.
   */
  revealOnly?: boolean;
}

export type ScanStatus = 'ready' | 'ambiguous' | 'partial' | 'not_found';

export interface ScanCandidate {
  role: string;
  name: string;
  /** Durable Playwright locator expression the agent can act on and cache. */
  locator: string;
  /** Enclosing row/section that names the candidate, when scoped. */
  anchor?: string;
  bbox?: { x: number; y: number; w: number; h: number };
  visible: boolean;
  enabled: boolean;
  /** `count() === 1` verified driver-side (top candidates only). */
  unique?: boolean;
  inViewport?: boolean;
}

export interface FastDriveScanResult {
  status: ScanStatus;
  /** The single confident target — set only on `ready`. */
  target?: ScanCandidate;
  /** Ranked candidates (the whole set on `ambiguous`/`partial`). */
  candidates: ScanCandidate[];
  /** Rare ephemeral handle: `page.locator('[data-fdref="<ref>"]')`. */
  ref?: string;
  /** Always present — tells the model its next move. */
  guidance: string;
}

// ── Budgets ──────────────────────────────────────────────────────────────────
const MAX_CANDIDATES = 5; // carried past the gate / returned
const MAX_DRIVER_PROBES = 3; // count-verified candidates
/**
 * Hard wall-clock ceiling for the WHOLE scan (traversal + verify + reveal).
 * Sized from live heavy-SPA measurement: the traversal + count-verify eat a few
 * hundred ms on a real page, and the winner still needs budget to scroll +
 * trial-confirm. Still ~1000× cheaper than the multi-turn reprobe it replaces,
 * and the hot failure-repair path is reveal-only (no trial), so it's faster.
 */
const SCAN_DEADLINE_MS = 1500;
/** Per-call reveal cap (scroll/trial), further clamped to the remaining budget. */
const REVEAL_CAP_MS = 500;
/** Rank gap (winner − runner-up, 0..1 scale) above which we call it `ready`. */
const READY_MARGIN = 0.15;

/** Optional debug logger (browser-core convention — a plain callback bag). */
interface ScanLogger {
  debug?: (msg: string, meta?: Record<string, unknown>) => void;
}

/**
 * Race a promise against a wall-clock deadline; the timer resolves to a sentinel.
 * When the timer wins, `p` is abandoned — its later settlement is still consumed
 * here (via the attached handlers) so it can never surface as an unhandled
 * rejection (e.g. a page.evaluate that errors after the scan already degraded).
 */
const DEADLINE_HIT = Symbol('scan-deadline');
function withDeadline<T>(p: Promise<T>, ms: number): Promise<T | typeof DEADLINE_HIT> {
  if (ms <= 0) {
    void p.catch(() => {}); // consume the abandoned promise's rejection, if any
    return Promise.resolve(DEADLINE_HIT);
  }
  return new Promise<T | typeof DEADLINE_HIT>((resolve, reject) => {
    const timer = setTimeout(() => resolve(DEADLINE_HIT), ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e); // no-op if the timer already resolved; the rejection is handled
      }
    );
  });
}

// ── Locator descriptors (page-side proposal → driver-side Locator) ────────────
type LocatorStep =
  | { by: 'testid'; value: string }
  | { by: 'role'; role: string; name?: string; exact?: boolean }
  | { by: 'label'; text: string; exact?: boolean }
  | { by: 'text'; text: string; exact?: boolean }
  | { by: 'css'; selector: string };
type LocatorDescriptor = LocatorStep[];

/** Raw candidate emitted by the single page-side traversal (pre driver-verify). */
interface RawCandidate {
  role: string;
  name: string;
  anchor?: string;
  bbox: { x: number; y: number; w: number; h: number };
  visible: boolean;
  enabled: boolean;
  inViewport: boolean;
  /**
   * The target itself bears a real affordance (interactive tag/role, pointer
   * cursor, testid, or wraps a single control) — not an inert label/text span.
   * The `ready` status is gated on this so scan never hands back a confident
   * locator for a non-actionable annotation.
   */
  affordance: boolean;
  /** 0..1 rank score (name-locality, affordance prior, size sanity). */
  score: number;
  /** Ordered durable-locator proposals; driver keeps the first `count()===1`. */
  proposals: LocatorDescriptor[];
}

/**
 * The page-side traversal's full result: the ranked candidates, plus a flag for
 * the `descendant`/`row` case where the text anchor WAS found but no matching
 * target control existed inside it — so the orchestrator can give the model
 * "anchor found, target missing" guidance instead of a misleading "not_found".
 */
interface PageSideScanResult {
  candidates: RawCandidate[];
  anchorMatchedNoTarget: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 1 — the single, self-contained, read-only DOM traversal.
// Runs in the browser via page.evaluate; references only its arg + DOM globals.
// Inlines every helper (normalization mirrors `normalizeComparableText` in
// icon-label-normalizer.ts — kept identical, pinned by test).
// ─────────────────────────────────────────────────────────────────────────────
function pageSideScan(input: {
  query: string;
  at?: { x: number; y: number };
  targetRole?: string;
  targetText?: string;
  relation: ScanRelation;
  maxCandidates: number;
}): PageSideScanResult {
  const NORM = (t: string | null | undefined): string =>
    (t ?? '')
      .replace(/[\uE000-\uF8FF]/g, ' ')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const want = NORM(input.query).toLowerCase();
  // Coordinate-probe mode needs no text; text mode requires a non-empty query.
  if (!input.at && !want) return { candidates: [], anchorMatchedNoTarget: false };

  const implicitRole = (el: Element): string => {
    const tag = el.tagName.toLowerCase();
    const explicit = el.getAttribute('role');
    if (explicit) return explicit;
    const map: Record<string, string> = {
      button: 'button',
      a: (el as HTMLAnchorElement).hasAttribute('href') ? 'link' : 'generic',
      select: 'combobox',
      textarea: 'textbox',
      img: 'img',
      table: 'table',
      tr: 'row',
      td: 'cell',
      th: 'columnheader',
      ul: 'list',
      ol: 'list',
      li: 'listitem',
      nav: 'navigation',
    };
    if (tag === 'input') {
      const type = (el.getAttribute('type') || 'text').toLowerCase();
      if (type === 'checkbox') return 'checkbox';
      if (type === 'radio') return 'radio';
      if (type === 'button' || type === 'submit') return 'button';
      return 'textbox';
    }
    return map[tag] ?? 'generic';
  };

  const accName = (el: Element): string => {
    const aria = el.getAttribute('aria-label');
    if (aria && NORM(aria)) return NORM(aria);
    const labelledby = el.getAttribute('aria-labelledby');
    if (labelledby) {
      const parts = labelledby
        .split(/\s+/)
        .map((id) => document.getElementById(id)?.textContent || '')
        .join(' ');
      if (NORM(parts)) return NORM(parts);
    }
    // associated <label for>
    const id = el.getAttribute('id');
    if (id) {
      const lbl = document.querySelector(`label[for="${CSS.escape(id)}"]`);
      if (lbl && NORM(lbl.textContent)) return NORM(lbl.textContent);
    }
    const inputVal = (el as HTMLInputElement).placeholder;
    if (inputVal && NORM(inputVal)) return NORM(inputVal);
    return NORM(el.textContent);
  };

  const rect = (el: Element) => el.getBoundingClientRect();
  const isVisible = (el: Element): boolean => {
    const r = rect(el);
    if (r.width <= 0 || r.height <= 0) return false;
    const style = getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0';
  };
  const isEnabled = (el: Element): boolean =>
    !(el as HTMLButtonElement).disabled && el.getAttribute('aria-disabled') !== 'true';

  const INTERACTIVE_SEL =
    'button,a[href],input,select,textarea,[role=button],[role=combobox],[role=checkbox],[role=radio],[role=tab],[role=menuitem],[role=option],[role=switch],[contenteditable=true],[tabindex]';

  const isBoundary = (el: Element): boolean => {
    if (el.matches(INTERACTIVE_SEL)) return true;
    if (getComputedStyle(el).cursor === 'pointer') return true;
    const r = rect(el);
    if (r.width > 0 && r.width < 600 && r.height > 0 && r.height < 160) {
      // A list/menu/popover wrapping multiple rows/options is a CONTAINER, not a
      // single control — its per-row buttons are reached via relation:"descendant".
      const groups = el.querySelectorAll('[role=row],[role=option],[role=menuitem],li,tr');
      if (groups.length >= 2) return false;
      const ctrls = el.querySelectorAll('input,button,[role=combobox]');
      if (ctrls.length >= 1 && ctrls.length <= 2) return true;
    }
    return false;
  };

  // A real affordance on the element ITSELF (not merely "a control sits nearby"):
  // interactive tag/role, a pointer cursor, an explicit testid, or a compact
  // wrapper around exactly one control. Used to gate `ready` off inert labels.
  const hasAffordance = (el: Element): boolean => {
    if (el.matches(INTERACTIVE_SEL)) return true;
    if (getComputedStyle(el).cursor === 'pointer') return true;
    if (el.getAttribute('data-testid') || el.getAttribute('data-test')) return true;
    return el.querySelector('input,button,select,textarea,[role=combobox]') !== null;
  };

  const snapUp = (el: Element): Element | null => {
    let n: Element | null = el;
    for (let i = 0; i < 8 && n && n !== document.body; i++) {
      if (isBoundary(n)) return n;
      n = n.parentElement;
    }
    return null;
  };

  const carriesQuery = (el: Element): boolean =>
    NORM(el.textContent).toLowerCase().includes(want) || accName(el).toLowerCase().includes(want);

  // Clear any lingering ephemeral stamps from a prior scan BEFORE we read the
  // DOM, so a stale `data-fdref` can never leak into this scan's results or into
  // a snapshot/cache capture. Free — we're already in this evaluate.
  document.querySelectorAll('[data-fdref]').forEach((e) => e.removeAttribute('data-fdref'));

  // 1. bounded find — seed the candidate set.
  const MATCH_CAP = 60;
  const matchSet = new Set<Element>();
  if (input.at) {
    // Coordinate-probe: the element the agent is pointing at (from a screenshot).
    // `snapUp` below walks it to the actionable control boundary, and the shared
    // proposal/verify pipeline turns it into a durable locator.
    const el = document.elementFromPoint(input.at.x, input.at.y);
    if (el) matchSet.add(el);
  } else {
    // Text mode — walk TEXT NODES rather than `querySelectorAll('*')` +
    // `textContent`. `textContent` re-serializes a container's whole subtree and
    // bubbles, so a naive scan inflates the match set to ~occurrences×depth and
    // re-reads large subtrees repeatedly. A TreeWalker over text nodes yields the
    // LEAF-most matches directly in O(n), capped at MATCH_CAP. A small extra pass
    // covers aria-labelled controls whose OWN text doesn't carry the query (MUI
    // puts the chip label in a sibling, so the combobox text is just "All (13)").
    const root = document.body || document.documentElement;
    if (root) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node: Node | null;
      while ((node = walker.nextNode())) {
        if (matchSet.size >= MATCH_CAP) break;
        const text = node.nodeValue;
        if (text && NORM(text).toLowerCase().includes(want)) {
          const parent = node.parentElement;
          if (parent) matchSet.add(parent);
        }
      }
    }
    if (matchSet.size < MATCH_CAP) {
      const labelled = document.querySelectorAll('[aria-label],[aria-labelledby]');
      for (let i = 0; i < labelled.length && matchSet.size < MATCH_CAP; i++) {
        if (accName(labelled[i]).toLowerCase().includes(want)) matchSet.add(labelled[i]);
      }
    }
  }
  const matches = Array.from(matchSet);

  const wantTargetRole = input.targetRole ? input.targetRole.toLowerCase() : '';
  const wantTargetText = input.targetText ? NORM(input.targetText).toLowerCase() : '';

  // 2. snap → boundary; 3. resolve action target (anchor ≠ target); 4. gate;
  //    5. describe + score. Dedup happens AFTER ranking (step 6) so that an
  //    ancestor/descendant pair keeps the higher-scored (more nameable) one —
  //    e.g. the role=combobox chip beats its generic MuiFormControl wrapper.
  const seen = new Set<Element>();
  const scored: { el: Element; cand: RawCandidate }[] = [];
  // Set when a boundary carried the query (anchor found) but no descendant target
  // matched — distinguishes "anchor found, target missing" from a true no-match.
  let anchorMatchedNoTarget = false;
  // Memoize the descendant-target lookup per scope: many query matches land in the
  // SAME row, and the lookup runs getComputedStyle (isBoundary) per descendant, so
  // re-scanning a wide row per match is the dominant cost on large tables.
  const descCache = new Map<Element, Element | null>();

  const rowScoped = input.relation === 'descendant' || input.relation === 'row';
  for (const m of matches) {
    // Follow a `<label for=…>` (or a wrapping `<label>`) to its control: for the
    // common `<label for="email">Email</label>` + sibling `<input id="email">`,
    // the query text lives on the label, but snapUp only walks ANCESTORS and would
    // miss the sibling input → not_found. `HTMLLabelElement.control` bridges to it.
    const lbl = m instanceof HTMLLabelElement ? m : m.closest('label');
    const seed = (lbl && (lbl as HTMLLabelElement).control) || m;

    let target: Element;
    let anchor: Element | null = null;
    if (rowScoped) {
      // Row/descendant: the anchor text only needs to be INSIDE a row — it need
      // NOT be a control itself. Plain cell text (e.g. "100ART4S" in a wide `<tr>`)
      // has no boundary ancestor, so requiring snapUp here would skip an otherwise-
      // resolvable per-row button. Scope to the enclosing row/list-item (falling
      // back to the anchor's control, or the anchor, for non-row containers).
      const anchorBoundary = snapUp(seed);
      const scope =
        seed.closest('[role=row],tr,li,[role=listitem]') ||
        anchorBoundary?.closest('[role=row],tr,li,[role=listitem]') ||
        anchorBoundary ||
        seed;
      let desc: Element | null | undefined = descCache.get(scope);
      if (desc === undefined) {
        // Cap the scan: a wide table row or virtualized list can hold hundreds of
        // descendants, and the predicate runs getComputedStyle/getBoundingClientRect
        // (forced reflow) on non-matches — keep it bounded so it can't dominate the
        // scan budget. `.find` still short-circuits on the first real match.
        desc =
          Array.from(scope.querySelectorAll('*'))
            .slice(0, 500)
            .find((d) => {
              if (d === seed || d === anchorBoundary) return false;
              if (wantTargetRole && implicitRole(d) !== wantTargetRole) return false;
              if (wantTargetText && !accName(d).toLowerCase().includes(wantTargetText)) return false;
              return isBoundary(d) || d.matches(INTERACTIVE_SEL);
            }) ?? null;
        descCache.set(scope, desc);
      }
      if (!desc) {
        anchorMatchedNoTarget = true;
        continue;
      }
      target = desc;
      // Anchor the locator to the tight, uniquely-named row around the target.
      anchor = desc.closest('[role=row],tr,li,[role=listitem]') || scope;
    } else {
      // self: the action target IS the control boundary around the anchor.
      const boundary = snapUp(seed);
      if (!boundary) continue;
      // compact gate (kills giant containers) + query-locality gate (kills
      // boundaries whose match is only a distant descendant).
      const br = rect(boundary);
      if (br.width > 600 || br.height > 160) continue;
      if (!carriesQuery(boundary)) continue;
      target = boundary;
    }
    if (seen.has(target)) continue;
    seen.add(target);

    const role = implicitRole(target);
    const name = accName(target);
    const r = rect(target);
    // testid on the target, or on its nearest compact ancestor — apps put a
    // stable `data-testid` on the clickable control WRAPPER (e.g. MUI's
    // `select-transaction-type` FormControl), while the query text often matches
    // an inner <label> or hidden <input>. Prefer that wrapper testid: it's both
    // durable AND actionable, unlike `getByLabel` (which resolves the hidden input).
    let testid = target.getAttribute('data-testid') || target.getAttribute('data-test') || '';
    if (!testid && !anchor) {
      // Walk up to 6 ancestors for a testid. Real apps (Fintary's filter chips,
      // MUI FormControls) put the stable `data-testid` on a control wrapper that
      // can sit several plain <div> layers above the element the query text /
      // coordinate-probe actually lands on. The compact gate (w<600, h<160) is the
      // safety rail — it rejects an over-broad container (a toolbar/section testid)
      // so the walk can reach the wrapper without ever grabbing the whole strip.
      let a: Element | null = target.parentElement;
      for (let i = 0; i < 6 && a && a !== document.body; i++) {
        const ar = rect(a);
        if (ar.width > 0 && ar.width < 600 && ar.height > 0 && ar.height < 160) {
          const tid = a.getAttribute('data-testid') || a.getAttribute('data-test');
          if (tid) {
            testid = tid;
            break;
          }
        }
        a = a.parentElement;
      }
    }
    const id = target.getAttribute('id') || '';
    // Name the row/list-item scope by the QUERY, not the anchor's raw textContent.
    // A plain `<tr>`'s textContent concatenates cells without spaces ("100ART4SSelect
    // only"), but Playwright's accessible row name is space-joined ("100ART4S Select
    // only") — so `getByRole('row', { name: <textContent> })` resolves to COUNT 0.
    // The query (e.g. "100ART4S") is why we're in this row and matches as a
    // substring (exact:false); it also keeps the $var binding substitutable.
    const anchorName = anchor ? (input.query?.trim() ? input.query.trim() : accName(anchor)) : '';
    const anchorRole = anchor ? implicitRole(anchor) : '';

    const proposals: LocatorDescriptor[] = [];
    const scopeSteps: LocatorStep[] =
      anchor && anchorName ? [{ by: 'role', role: anchorRole, name: anchorName }] : [];
    // Prefer semantic locators (testid → role+name → label) over a raw #id —
    // many framework ids are generated (MUI `:r1:`, already excluded by the
    // regex) and even stable-looking ones are less legible than role+name.
    if (testid) proposals.push([...scopeSteps, { by: 'testid', value: testid }]);
    if (name) {
      // Skip getByRole for the `generic` role: on a large DOM `getByRole('generic',
      // { name })` is expensive (it computes accessible names across the tree) AND
      // rarely resolves — a double cost we can't afford in the verify budget.
      if (role !== 'generic') {
        proposals.push([...scopeSteps, { by: 'role', role, name, exact: false }]);
        proposals.push([...scopeSteps, { by: 'role', role, name, exact: true }]);
      }
      if (!anchor) proposals.push([{ by: 'label', text: name, exact: false }]);
    }
    if (!anchor && id && /^[A-Za-z][\w-]*$/.test(id)) proposals.push([{ by: 'css', selector: `#${id}` }]);
    if (name && !anchor) proposals.push([{ by: 'text', text: name, exact: true }]);

    // score: name-locality (own name == query) + affordance prior + size sanity.
    const nname = NORM(name).toLowerCase();
    let score = 0.4;
    if (nname === want) score += 0.35;
    else if (nname.startsWith(want) || nname.includes(want)) score += 0.15;
    if (['combobox', 'button', 'textbox', 'checkbox', 'menuitem', 'option', 'link', 'tab'].includes(role))
      score += 0.15;
    if (role === 'columnheader' || role === 'cell') score -= 0.25; // a table header, not the control
    // A bare <label> is an annotation, not the action target — its control is
    // (clicking the label at best focuses a hidden input). Deprioritize it so a
    // sibling wrapper (e.g. the testid'd FormControl) wins the filter chip.
    if (target.tagName === 'LABEL') score -= 0.3;
    if (r.width > 0 && r.width < 400) score += 0.05;
    // A data-testid (on the target OR a close wrapper) yields `getByTestId` — the
    // app's explicit, actionable+durable handle and the single most reliable
    // anchor — so a candidate that can use it strongly outranks a bare label/text
    // match (e.g. MUI's `<label>` sibling in a duplicate FormControl that only
    // resolves a hidden input via getByLabel). Own vs ancestor is weighted the
    // same: what matters is that the winning LOCATOR is the testid. Weighted
    // ABOVE an exact-name match — a testid is explicit and unique by intent,
    // while an accessible name can be duplicated (MUI renders the chip label
    // twice) or resolve a hidden input via getByLabel.
    if (testid) score += 0.45;
    if (proposals.length === 0) score -= 0.3;
    score = Math.max(0, Math.min(1, score));

    scored.push({
      el: target,
      cand: {
        role,
        name,
        anchor: anchorName || undefined,
        bbox: { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.width), h: Math.round(r.height) },
        visible: isVisible(target),
        enabled: isEnabled(target),
        inViewport: r.x >= 0 && r.y >= 0 && r.right <= innerWidth && r.bottom <= innerHeight,
        affordance: hasAffordance(target),
        score,
        proposals,
      },
    });
  }

  // 6. rank, then dedup ancestor/descendant pairs keeping the higher-scored.
  scored.sort((a, b) => b.cand.score - a.cand.score);
  const kept: RawCandidate[] = [];
  const keptEls: Element[] = [];
  for (const s of scored) {
    if (keptEls.some((k) => k.contains(s.el) || s.el.contains(k))) continue;
    kept.push(s.cand);
    keptEls.push(s.el);
    if (kept.length >= input.maxCandidates) break;
  }
  // The anchor-only signal is only meaningful when NOTHING was kept — if we found
  // a real target, the miss on some other anchor is noise.
  return { candidates: kept, anchorMatchedNoTarget: kept.length === 0 && anchorMatchedNoTarget };
}

// ─────────────────────────────────────────────────────────────────────────────
// Driver-side helpers.
// ─────────────────────────────────────────────────────────────────────────────
function applyStep(root: Page | Locator, step: LocatorStep): Locator {
  switch (step.by) {
    case 'testid':
      return root.getByTestId(step.value);
    case 'role':
      return root.getByRole(
        step.role as Parameters<Page['getByRole']>[0],
        step.name ? { name: step.name, exact: step.exact } : undefined
      );
    case 'label':
      return root.getByLabel(step.text, { exact: step.exact });
    case 'text':
      return root.getByText(step.text, { exact: step.exact });
    case 'css':
      return root.locator(step.selector);
  }
}

function buildLocator(page: Page, desc: LocatorDescriptor): Locator {
  let loc: Locator | undefined;
  for (const step of desc) loc = applyStep(loc ?? page, step);
  // desc is never empty (proposals always have ≥1 step)
  return loc as Locator;
}

function stepToString(step: LocatorStep): string {
  const q = (s: string) => JSON.stringify(s);
  switch (step.by) {
    case 'testid':
      return `.getByTestId(${q(step.value)})`;
    case 'role': {
      if (!step.name) return `.getByRole(${q(step.role)})`;
      const opts = step.exact ? `, { name: ${q(step.name)}, exact: true }` : `, { name: ${q(step.name)} }`;
      return `.getByRole(${q(step.role)}${opts})`;
    }
    case 'label':
      return `.getByLabel(${q(step.text)}${step.exact ? ', { exact: true }' : ''})`;
    case 'text':
      return `.getByText(${q(step.text)}${step.exact ? ', { exact: true }' : ''})`;
    case 'css':
      return `.locator(${q(step.selector)})`;
  }
}

/**
 * A safe binding expression: a JS identifier / property-access path (e.g.
 * `vars.option_three`, `attach.row`). This is spliced UNQUOTED into the returned
 * cacheCode string, which is later persisted and executed as Playwright code — so
 * the engine validates it here rather than trusting the caller. Anything with
 * quotes, backticks, parens, whitespace, or operators is rejected and the locator
 * keeps the literal (a stale-but-safe value beats an injection).
 */
const SAFE_BINDING = /^[A-Za-z_$][A-Za-z0-9_$]*(?:(?:\.[A-Za-z_$][A-Za-z0-9_$]*)|(?:\[\d+\]))*$/;

function describeLocator(desc: LocatorDescriptor, queryBinding: string | undefined, resolvedQuery: string): string {
  const s = 'page' + desc.map(stepToString).join('');
  // Preserve dynamic binding: swap the resolved literal back to the var expr so
  // cacheCode stays dynamic. Only the FIRST occurrence is rewritten — never
  // clobber a coincidental second match (e.g. target name == anchor name).
  if (!queryBinding || !resolvedQuery || !SAFE_BINDING.test(queryBinding)) return s;

  // Case 1 — a string arg EQUALS the query (`name: "100ART4S"`): swap the whole
  // quoted literal for the bare binding.
  const exact = JSON.stringify(resolvedQuery);
  if (s.includes(exact)) return s.replace(exact, queryBinding);

  // Case 2 — a string arg CONTAINS the query (`name: "Account 100ART4S"`): a plain
  // replace of `"100ART4S"` is a no-op (the quotes aren't adjacent), so cacheCode
  // would hardcode today's row. Rewrite that ONE quoted literal into a template
  // literal that interpolates the binding, keeping the surrounding text.
  let done = false;
  return s.replace(/"(?:[^"\\]|\\.)*"/g, (lit) => {
    if (done) return lit;
    let raw: string;
    try {
      raw = JSON.parse(lit) as string;
    } catch {
      return lit;
    }
    const idx = raw.indexOf(resolvedQuery);
    if (idx < 0) return lit;
    done = true;
    // Escape backslashes, backticks, and `${` in the literal parts so they can't
    // break out of the template or start an unintended interpolation.
    const esc = (t: string) => t.replace(/[\\`]/g, '\\$&').replace(/\$\{/g, '\\${');
    const before = esc(raw.slice(0, idx));
    const after = esc(raw.slice(idx + resolvedQuery.length));
    return '`' + before + '${' + queryBinding + '}' + after + '`';
  });
}

function toCandidate(raw: RawCandidate, locator: string, unique: boolean | undefined): ScanCandidate {
  return {
    role: raw.role,
    name: raw.name,
    locator,
    anchor: raw.anchor,
    bbox: raw.bbox,
    visible: raw.visible,
    enabled: raw.enabled,
    unique,
    inViewport: raw.inViewport,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Orchestrator (driver side). One page.evaluate; bounded driver probes; reveal
// + trial-confirm the winner only; gated ephemeral-ref fallback.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Page-side: re-find the winning candidate at its recorded position and stamp an
 * ephemeral `data-fdref="f0"` on it (clearing any prior stamp first), then scroll
 * it into view. Returns the ref id, or '' if it can't be re-found.
 *
 * Re-find is a cheap HIT-TEST at the winner's center (`elementFromPoint`), not a
 * whole-document `querySelectorAll('*')`+`textContent` scan \u2014 that would re-read
 * every subtree (the exact O(occurrences\u00D7depth) trap `pageSideScan` was written to
 * avoid), and this path fires on precisely the poorly-instrumented, often-large
 * pages scan most needs to stay cheap on. A CAPPED text-node TreeWalker is the
 * fallback for a winner that's scrolled out of the viewport (where hit-testing its
 * pixel returns nothing). Both a POSITION match (\u00B14px) and a TEXT match are
 * required, so a same-text element elsewhere on the page can't be mis-stamped.
 * The normalizer mirrors `pageSideScan`'s `NORM`.
 */
function stampWinner(b: { name: string; x: number; y: number; w: number; h: number }): string {
  const norm = (t: string | null): string =>
    (t ?? '')
      .replace(/[\uE000-\uF8FF]/g, ' ')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  document.querySelectorAll('[data-fdref]').forEach((e) => e.removeAttribute('data-fdref'));
  const want = norm(b.name);
  const matches = (el: Element): boolean => {
    const r = el.getBoundingClientRect();
    return Math.abs(r.x - b.x) < 4 && Math.abs(r.y - b.y) < 4 && (!want || norm(el.textContent).includes(want));
  };
  let pick: Element | null = null;
  // Primary: O(1) hit-test at the winner's center, then a short ancestor walk to
  // the element whose box matches (elementFromPoint returns the deepest node).
  const hit = document.elementFromPoint(Math.round(b.x + b.w / 2), Math.round(b.y + b.h / 2));
  for (let n: Element | null = hit; n && n !== document.body; n = n.parentElement) {
    if (matches(n)) {
      pick = n;
      break;
    }
  }
  // Fallback for an off-screen winner (its center isn't hit-testable): a CAPPED
  // text-node walk \u2014 never the unbounded querySelectorAll('*') scan.
  if (!pick && want) {
    const root = document.body || document.documentElement;
    if (root) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node: Node | null;
      let scanned = 0;
      while ((node = walker.nextNode()) && scanned < 200) {
        scanned++;
        if (node.nodeValue && norm(node.nodeValue).includes(want)) {
          const parent = node.parentElement;
          if (parent && matches(parent)) {
            pick = parent;
            break;
          }
        }
      }
    }
  }
  if (!pick) return '';
  pick.setAttribute('data-fdref', 'f0');
  pick.scrollIntoView({ block: 'center', inline: 'center' });
  return 'f0';
}

export async function runFastDriveScan(args: {
  getPage: () => Promise<Page>;
  raceAgainstSignal: RaceAgainstSignal;
  signal?: AbortSignal;
  input: FastDriveScanInput;
  /** Ephemeral-ref stamping kill-switch (cross-process, host-configured). */
  stampingEnabled: boolean;
  /** Optional debug logger — silent degradations are logged for prod visibility. */
  logger?: ScanLogger;
  /** Wall-clock ceiling override (default {@link SCAN_DEADLINE_MS}); for tests. */
  budgetMs?: number;
}): Promise<FastDriveScanResult> {
  const { getPage, raceAgainstSignal, signal, input, logger } = args;
  const relation = input.relation ?? 'self';
  const deadline = Date.now() + (args.budgetMs ?? SCAN_DEADLINE_MS);
  const remaining = () => Math.max(0, deadline - Date.now());
  const describe = (d: LocatorDescriptor | undefined) =>
    d ? describeLocator(d, input.queryBinding, input.query ?? '') : '(no durable locator)';

  // Single try/catch so a mid-scan page close / evaluate crash NEVER throws — the
  // primitive's contract is to always return a status, never dead-end the model.
  try {
    const page = await getPage();

    // Phase 1: one bounded, query-scoped DOM traversal, hard-raced against the
    // wall-clock budget (it scales with DOM size and is the most expensive step).
    const raced = await withDeadline(
      raceAgainstSignal('fast-drive-scan.find', signal, () =>
        page.evaluate(pageSideScan, {
          query: input.query ?? '',
          at: input.at,
          targetRole: input.targetRole,
          targetText: input.targetText,
          relation,
          maxCandidates: MAX_CANDIDATES,
        })
      ),
      remaining()
    );
    if (raced === DEADLINE_HIT) {
      logger?.debug?.('[fast-drive-scan] traversal hit deadline', { query: input.query });
      return { status: 'partial', candidates: [], guidance: guidanceFor('partial', input, 0) };
    }
    const raws = raced.candidates;
    if (!raws || raws.length === 0) {
      // Distinguish "anchor found but its target control is missing" (descendant/
      // row path) from a true no-match, so the model hovers/re-scans rather than
      // assuming the anchor text itself was wrong.
      const notFoundGuidance = raced.anchorMatchedNoTarget
        ? `Anchor ${JSON.stringify(input.query ?? '')} found, but no ${
            input.targetText ? `${JSON.stringify(input.targetText)} ` : ''
          }${input.targetRole ?? 'control'} inside it — the target may be nameless or revealed on hover. Hover the anchor/row then re-scan, or drop \`targetText\`.`
        : guidanceFor('not_found', input, 0);
      return { status: 'not_found', candidates: [], guidance: notFoundGuidance };
    }

    // Phase 2: verify the top few — `count()` is instant (no wait), so this is
    // cheap; still bail to `partial` if the wall-clock is spent. Collect ALL the
    // count()===1 proposals per candidate (capped) so the reveal step can try an
    // ACTIONABLE one — a `getByLabel` may uniquely resolve to a hidden input,
    // while a `getByTestId`/`getByRole` on the same candidate hits the clickable
    // wrapper.
    const verified: {
      raw: RawCandidate;
      locator: string;
      unique: boolean;
      uniques: { desc: LocatorDescriptor; loc: Locator }[];
    }[] = [];
    for (let i = 0; i < raws.length && i < MAX_DRIVER_PROBES; i++) {
      if (remaining() <= 0) break;
      const raw = raws[i];
      const uniques: { desc: LocatorDescriptor; loc: Locator }[] = [];
      for (const desc of raw.proposals) {
        if (uniques.length >= 2) break;
        // Budget check per PROPOSAL, not just per candidate: a candidate with many
        // slow count() round-trips must not starve the remaining candidates.
        if (remaining() <= 0) break;
        try {
          const loc = buildLocator(page, desc);
          const n = await raceAgainstSignal('fast-drive-scan.count', signal, () => loc.count());
          if (n === 1) uniques.push({ desc, loc });
        } catch {
          // try next proposal
        }
      }
      verified.push({
        raw,
        locator: uniques[0] ? describe(uniques[0].desc) : describe(raw.proposals[0]),
        unique: uniques.length > 0,
        uniques,
      });
    }

    // Candidates beyond the probe cap (or unreached due to deadline) still get
    // surfaced for the ambiguous/partial set, marked unverified.
    const extras = raws.slice(verified.length).map((r) => toCandidate(r, describe(r.proposals[0]), undefined));
    const allCands = () => verified.map((v) => toCandidate(v.raw, v.locator, v.unique)).concat(extras);

    // Deadline spent before we could verify the top set -> partial, but KEEP the
    // verified-so-far unique markers rather than discarding them.
    if (remaining() <= 0 && verified.length < Math.min(raws.length, MAX_DRIVER_PROBES)) {
      const cands = allCands();
      return { status: 'partial', candidates: cands, guidance: guidanceFor('partial', input, cands.length) };
    }

    const uniqueVerified = verified.filter((v) => v.unique);
    const top = verified[0];
    const margin = verified.length >= 2 ? verified[0].raw.score - verified[1].raw.score : 1;

    // READY: the top candidate has a unique durable locator, bears a real
    // affordance (not an inert label/header — the affordance-gate), is ENABLED (a
    // disabled control isn't an action target — returning it `ready` just sends the
    // agent into a click timeout), AND is a clear winner (only candidate, or
    // decisively ahead on score). Anything else → ambiguous.
    if (top?.unique && top.raw.affordance && top.raw.enabled && (verified.length === 1 || margin >= READY_MARGIN)) {
      // Reveal-and-confirm across the candidate's unique proposals: pick the first
      // that scrolls into view AND is visible. We use `scrollIntoViewIfNeeded` +
      // `isVisible` rather than a trial-click on purpose: a `getByLabel` that
      // resolves a hidden 0-size input fails `scrollIntoViewIfNeeded` (rejected),
      // while a clickable MUI wrapper (whose real click works via child
      // event-bubbling) passes — a trial-click would FALSE-NEGATIVE the wrapper
      // because its "receives pointer events" hit-test is stricter than a real
      // click. The agent's own retry does the final actionability check.
      let picked: { desc: LocatorDescriptor; loc: Locator } | null = null;
      let revealed = false;
      const canReveal = input.allowReveal !== false;
      if (canReveal) {
        // The winner reveal gets its OWN budget, separate from the find/verify
        // deadline — otherwise a slow-verify page starves the (cheap, single-
        // winner) reveal and we needlessly downgrade a real target to ambiguous.
        const revealDeadline = Date.now() + 2 * REVEAL_CAP_MS;
        for (const cand of top.uniques) {
          const t = Math.min(REVEAL_CAP_MS, revealDeadline - Date.now());
          if (t <= 0) break;
          try {
            const ok = await raceAgainstSignal('fast-drive-scan.reveal', signal, async () => {
              await cand.loc.scrollIntoViewIfNeeded({ timeout: t });
              return await cand.loc.isVisible();
            });
            if (!ok) continue; // resolved but not visible (hidden control) — next
            picked = cand;
            revealed = true;
            break;
          } catch (err) {
            logger?.debug?.('[fast-drive-scan] proposal not revealable; trying next', {
              query: input.query,
              error: err instanceof Error ? err.message : String(err),
            });
          }
        }
        // Non-repair: if NO unique proposal could be revealed as visible, don't
        // claim `ready` (we'd be handing the agent a confirmed-hidden locator).
        // Fall through to `ambiguous` so the model looks. The repair path
        // (`revealOnly`) tolerates a reveal miss and still advises.
        if (!revealed && !input.revealOnly) {
          logger?.debug?.('[fast-drive-scan] winner not actionable; downgrading to ambiguous', {
            query: input.query,
          });
          const cands = allCands();
          return { status: 'ambiguous', candidates: cands, guidance: guidanceFor('ambiguous', input, cands.length) };
        }
      }
      const chosen = picked ?? top.uniques[0];
      const target = toCandidate(top.raw, describe(chosen.desc), true);
      target.inViewport = revealed ? true : top.raw.inViewport;
      if (revealed && picked) {
        // Reveal scrolled the control, so the traversal-time bbox is stale. Refresh
        // it from the live locator, else the context crop (Improvement C) outlines
        // the pre-scroll coordinates — the wrong page region.
        const box = await picked.loc.boundingBox().catch(() => null);
        if (box) {
          target.bbox = { x: Math.round(box.x), y: Math.round(box.y), w: Math.round(box.width), h: Math.round(box.height) };
        }
      }
      return { status: 'ready', target, candidates: [target], guidance: guidanceFor('ready', input, 1) };
    }

    // No unique durable locator anywhere -> rare, gated ephemeral-ref fallback.
    // Only stamp when we know WHICH element is intended: a coordinate-probe (the
    // agent pointed at a specific pixel) or a single / decisively-winning candidate.
    // For an ambiguous tie (e.g. two unscoped "Edit" buttons with identical
    // proposals) stamping the DOM-first one would silently act on the wrong row —
    // fall through to `ambiguous` and let the model disambiguate instead.
    const stampTargetIsClear = !!input.at || verified.length === 1 || margin >= READY_MARGIN;
    if (
      uniqueVerified.length === 0 &&
      stampTargetIsClear &&
      input.allowStamp &&
      args.stampingEnabled &&
      top &&
      remaining() > 0
    ) {
      try {
        const ref = await withDeadline(
          raceAgainstSignal('fast-drive-scan.stamp', signal, () =>
            page.evaluate(stampWinner, {
            name: top.raw.name,
            x: top.raw.bbox.x,
            y: top.raw.bbox.y,
            w: top.raw.bbox.w,
            h: top.raw.bbox.h,
          })
          ),
          remaining()
        );
        if (ref && ref !== DEADLINE_HIT) {
          const target = toCandidate(top.raw, `page.locator('[data-fdref="${ref}"]')`, true);
          return {
            status: 'ready',
            target,
            candidates: [target],
            ref,
            guidance:
              'No durable locator exists - using a short-lived ref. Act on `target.locator` now; do NOT report it as cacheCode (it will not replay). If this recurs, add a data-testid or a page skill.',
          };
        }
      } catch (err) {
        logger?.debug?.('[fast-drive-scan] stamp fallback failed', {
          query: input.query,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }

    // Otherwise: ambiguous — expose the ranked set + how to narrow. No scroll.
    const cands = allCands();
    return { status: 'ambiguous', candidates: cands, guidance: guidanceFor('ambiguous', input, cands.length) };
  } catch (err) {
    // getPage()/evaluate threw (page closed/crashed mid-scan) — never propagate.
    logger?.debug?.('[fast-drive-scan] scan threw; returning not_found', {
      query: input.query,
      error: err instanceof Error ? err.message : String(err),
    });
    return { status: 'not_found', candidates: [], guidance: guidanceFor('not_found', input, 0) };
  }
}
